import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { cloneDeep } from 'lodash'
import { DragDropProvider } from '@dnd-kit/react'
import { useSortable, isSortable } from '@dnd-kit/react/sortable'
import { useTranslation } from 'react-i18next'

import TextField from '../../../../../components/FormLegacy/FormFieldsV2/TextField'
import Icon from '../../../../../components/Icons'
import DataInput from '../components/DataInput'
import SetFormStructureModal from '../modals/SetFormStructureModal'
import AddProviderModal from '../modals/AddProviderModal'
import EditorText from '../../../../../components/EditorText/EditorText'
import { getDataInputTypes, getTaskInfoTypes, reorder, DATA_INPUT } from '../../helpers'
import { generateId } from '../../../../../utils'
import api from '../../../../../api/api'

const MAX_NAME_LENGTH = 50
const MAX_INPUTS_COUNT = 20

const SortableDataInput = ({ id, index, children }) => {
  const { ref } = useSortable({ id, index })
  return (
    <div
      ref={ref}
      className="checklist-modal-input-row"
      style={{ left: 'auto !important', marginBottom: 12 }}
    >
      {children}
    </div>
  )
}

const TaskData = ({
  disabled = false,
  onBack = () => {},
  handleFieldChange = () => {},
  onHandleNext = () => {},
  nameError,
  noInputsError,
  formState,
  isEdit,
  selectedBuilding,
  yardiCustomRecords,
  setYardiCustomRecords,
  setNameError = () => {},
}) => {
  const { t } = useTranslation(['checklists-create-task', 'common'])
  const [inputFormIndex, setInputFormIndex] = useState(null)
  const [addProviderIndex, setAddProviderIndex] = useState(null)
  const [editProviderIndex, setEditProviderIndex] = useState(null)

  const onInputsChange = (updated) =>
    handleFieldChange({ target: { name: 'inputs', value: updated } })

  const onFieldChange = (index, field, value) => {
    const newInputs = cloneDeep(formState.inputs)
    newInputs[index] = { ...newInputs[index], [field]: value }
    onInputsChange(newInputs)
  }

  const onYardiSyncFieldsChange = (
    index,
    { yardiOtherOccupantCustomRecord, yardiResidentCustomRecord, fields },
  ) => {
    const newInputs = cloneDeep(formState.inputs)
    newInputs[index] = {
      ...newInputs[index],
      yardiOtherOccupantCustomRecord,
      yardiResidentCustomRecord,
    }
    onInputsChange(newInputs)
    setYardiCustomRecords(fields)
  }

  const onAddDataInput = () => {
    const newInputs = cloneDeep(formState.inputs)
    newInputs.push({
      ...DATA_INPUT,
      inputType: getDataInputTypes(false, [], t)[0].value,
      _id: generateId(),
    })
    onInputsChange(newInputs)
  }

  const onAddTaskInfo = () => {
    const newInputs = cloneDeep(formState.inputs)
    newInputs.push({ ...DATA_INPUT, inputType: getTaskInfoTypes(t)[0].value, _id: generateId() })
    onInputsChange(newInputs)
  }

  const onRemoveDataInput = (index) => {
    const newInputs = cloneDeep(formState.inputs)
    newInputs.splice(index, 1)
    onInputsChange(newInputs)
  }

  const onCopyDataInput = (index) => {
    if (formState.inputs.length >= MAX_INPUTS_COUNT) return
    const newInputs = cloneDeep(formState.inputs)
    newInputs.splice(index, 0, { ...newInputs[index], _id: generateId() })
    onInputsChange(newInputs)
  }

  const onAddOption = (index) => {
    const newInputs = cloneDeep(formState.inputs)
    newInputs[index].options.push('')
    onInputsChange(newInputs)
  }

  const onRemoveOption = (index, optionIndex) => {
    const newInputs = cloneDeep(formState.inputs)
    newInputs[index].options.splice(optionIndex, 1)
    onInputsChange(newInputs)
  }

  const onChangeOption = (index, optionIndex, value) => {
    const newInputs = cloneDeep(formState.inputs)
    newInputs[index].options[optionIndex] = value
    onInputsChange(newInputs)
  }

  const setInputFrom = (inputs) => {
    const newInputs = cloneDeep(formState.inputs)
    newInputs[inputFormIndex] = { ...newInputs[inputFormIndex], form: inputs, error: null }
    onInputsChange(newInputs)
    setInputFormIndex(null)
  }

  const addProvider = (provider) => {
    const newInputs = cloneDeep(formState.inputs)
    newInputs[addProviderIndex].providersInfo.push(provider)
    onInputsChange(newInputs)
    setInputFormIndex(null)
  }

  const removeProvider = (index, providerIndex) => {
    const provider = formState.inputs[index].providersInfo[providerIndex]
    if (provider.id) {
      api.deleteUtilityProvider(provider.id)
    }
    const newInputs = cloneDeep(formState.inputs)
    newInputs[index].providersInfo.splice(providerIndex, 1)
    onInputsChange(newInputs)
  }

  const onEditProvider = (updatedProvider) => {
    const newInputs = cloneDeep(formState.inputs)
    newInputs[addProviderIndex].providersInfo[editProviderIndex] = updatedProvider
    onInputsChange(newInputs)
  }

  const onCloseAddProvider = () => {
    setAddProviderIndex(null)
    setEditProviderIndex(null)
  }

  const onDragEnd = (event) => {
    const { operation, canceled } = event
    if (canceled) return
    const { source } = operation
    if (!isSortable(source)) return
    const sourceIndex = source.initialIndex
    const targetIndex = source.index
    if (sourceIndex === targetIndex) return
    const updatedInputs = reorder([...formState.inputs], sourceIndex, targetIndex)
    onInputsChange(updatedInputs)
  }

  const onProviderDragEnd = (index, draggable) => {
    if (draggable.destination) {
      const updatedProviders = reorder(
        [...formState.inputs[index].providersInfo],
        draggable.source.index,
        draggable.destination.index,
      )
      const newInputs = cloneDeep(formState.inputs)
      newInputs[index].providersInfo = updatedProviders
      onInputsChange(newInputs)
    }
  }

  const handleNameChange = (e) => {
    if (e.target.value.length < MAX_NAME_LENGTH) {
      if (e.target.value.length > 0) {
        setNameError('')
      }
      handleFieldChange(e)
    }
  }

  const onChangeEditorText = (content, text) => {
    handleFieldChange({
      target: { name: 'instructions', value: text.trim() ? content : undefined },
    })
  }

  return (
    <React.Fragment>
      <div className="checklist-modal-content checklist-task-data">
        <div className="checklist-modal-input-row">
          <div className="input-container">
            <div className="input-label">
              {t('checklists-create-task:customTaskForm.taskNameLabel')}
            </div>
            <TextField
              name="name"
              onChange={handleNameChange}
              value={formState.name}
              placeholder={t('checklists-create-task:customTaskForm.taskNamePlaceholder')}
              style={{ width: '100%' }}
              isError={!!nameError}
              errorLabel={nameError}
              disabled={disabled}
            />
            <span className="input-desc-label">
              {t('checklists-create-task:customTaskForm.maxLengthInfo', {
                length: MAX_NAME_LENGTH,
              })}
            </span>
          </div>
        </div>
        <div className="checklist-modal-input-row">
          <div className="input-container">
            <div className="input-label">
              {t('checklists-create-task:customTaskForm.instructionsLabel')}
            </div>
            <EditorText
              onChange={onChangeEditorText}
              value={formState.instructions}
              placeholder={t('checklists-create-task:customTaskForm.instructionsPlaceholder')}
              disabled={disabled}
            />
          </div>
        </div>
        {noInputsError && <span className="error-label">{noInputsError}</span>}
        <div className="checklist-task-data__inputs-wrap">
          <DragDropProvider onDragEnd={onDragEnd}>
            <div style={{ width: '100%' }}>
              {formState.inputs.map((dataInput, index) => (
                <SortableDataInput key={dataInput._id} id={dataInput._id} index={index}>
                  <DataInput
                    onFieldChange={(field, value) => onFieldChange(index, field, value)}
                    onRemove={() => onRemoveDataInput(index)}
                    onCopy={() => onCopyDataInput(index)}
                    onAddOption={() => onAddOption(index)}
                    onRemoveOption={(optionIndex) => onRemoveOption(index, optionIndex)}
                    onChangeOption={(optionIndex, value) =>
                      onChangeOption(index, optionIndex, value)
                    }
                    onShowSetFrom={setInputFormIndex}
                    onShowAddProvider={() => setAddProviderIndex(index)}
                    onRemoveProvider={(providerIndex) => removeProvider(index, providerIndex)}
                    onEditProvider={(providerIndex) => {
                      setEditProviderIndex(providerIndex)
                      setAddProviderIndex(index)
                    }}
                    onProviderDragEnd={(draggable) => onProviderDragEnd(index, draggable)}
                    dataInputIndex={index}
                    dataInputState={dataInput}
                    selectedBuilding={selectedBuilding}
                    yardiCustomRecords={yardiCustomRecords}
                    setYardiCustomRecords={setYardiCustomRecords}
                    onYardiSyncFieldsChange={(value) => onYardiSyncFieldsChange(index, value)}
                    inputs={formState.inputs}
                    disabled={disabled}
                  />
                </SortableDataInput>
              ))}
            </div>
          </DragDropProvider>
        </div>
        <div className="checklist-modal-input-row">
          <div className="inline-row">
            <div>
              <button
                className="button button-black-outline"
                onClick={onAddDataInput}
                style={{ marginRight: 8 }}
                disabled={formState.inputs.length >= MAX_INPUTS_COUNT || disabled}
              >
                <Icon icon="downLeftLayout" />
                {t('checklists-create-task:dataInput.addDataInputButton')}
              </button>
              <button
                className="button button-black-outline"
                onClick={onAddTaskInfo}
                disabled={formState.inputs.length >= MAX_INPUTS_COUNT || disabled}
              >
                <Icon icon="iconT" />
                {t('checklists-create-task:taskData.addTaskInfoButton')}
              </button>
            </div>
          </div>
        </div>
      </div>
      {isEdit ? (
        <div className="checklist-modal-actions">
          <button className="button button-gray" onClick={onBack}>
            {t('common:actions.back')}
          </button>
          <button className="button button-primary auto-width" onClick={onHandleNext}>
            {t('common:actions.save')}
          </button>
        </div>
      ) : (
        <div className="checklist-modal-actions">
          <button className="button button-gray" onClick={onBack}>
            {t('common:actions.back')}
          </button>
          <button className="button button-primary auto-width" onClick={onHandleNext}>
            {t('checklists-create-task:shared.nextButton')}
          </button>
        </div>
      )}
      {inputFormIndex !== null && (
        <SetFormStructureModal
          onClose={() => setInputFormIndex(null)}
          onSubmit={setInputFrom}
          formState={formState.inputs[inputFormIndex].form}
        />
      )}
      {addProviderIndex !== null && (
        <AddProviderModal
          onClose={onCloseAddProvider}
          onAddProvider={addProvider}
          onEditProvider={onEditProvider}
          provider={
            editProviderIndex !== null
              ? formState.inputs[addProviderIndex].providersInfo[editProviderIndex]
              : null
          }
        />
      )}
    </React.Fragment>
  )
}

TaskData.propTypes = {
  disabled: PropTypes.bool,
  onBack: PropTypes.func,
  handleFieldChange: PropTypes.func,
  onHandleNext: PropTypes.func,
  formState: PropTypes.object,
  isEdit: PropTypes.bool,
  nameError: PropTypes.string,
  noInputsError: PropTypes.string,
  selectedBuilding: PropTypes.object,
  yardiCustomRecords: PropTypes.object,
  setYardiCustomRecords: PropTypes.func,
  setNameError: PropTypes.func,
}

export default TaskData
